import { fabric } from 'fabric';
import { tintImage } from './brandingLogo';

// Default placement when first added: centered, ~30% width, no rotation.
export const OVERLAY_DEFAULTS = {
  xPercent: 50,
  yPercent: 50,
  rotation: 0,
  scalePercent: 30,
};

// Add (or replace) an overlay image on a fabric canvas.
// `config` is the stored overlayImage object: { url, color, xPercent, yPercent,
// rotation, scalePercent }. `overlayId` keys the overlay so multiple instances
// can co-exist on the same canvas — passing the same id replaces just that one.
// If `color` is set, the image is tinted; otherwise the original colors are kept.
export async function addOverlayToCanvas(canvas, config, naturalW, naturalH, interactive = false, overlayId = 'main') {
  if (!config?.url) return null;
  const existing = canvas.getObjects().find(o => o._isOverlayImage && o._overlayId === overlayId);
  if (existing) canvas.remove(existing);

  const xPercent = config.xPercent ?? OVERLAY_DEFAULTS.xPercent;
  const yPercent = config.yPercent ?? OVERLAY_DEFAULTS.yPercent;
  const rotation = config.rotation ?? OVERLAY_DEFAULTS.rotation;
  const scalePercent = config.scalePercent ?? OVERLAY_DEFAULTS.scalePercent;

  const sourceUrl = config.color ? await tintImage(config.url, config.color) : config.url;

  return new Promise(resolve => {
    fabric.Image.fromURL(sourceUrl, (img) => {
      const targetWidth = (scalePercent / 100) * naturalW;
      const scale = img.width ? targetWidth / img.width : 1;
      img.set({
        left: (xPercent / 100) * naturalW,
        top: (yPercent / 100) * naturalH,
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale,
        angle: rotation,
        opacity: 1,
        selectable: interactive,
        evented: interactive,
        hasControls: interactive,
        lockUniScaling: true,
      });
      img._isOverlayImage = true;
      img._overlayId = overlayId;
      canvas.add(img);
      // Place above the branding logo (if any) but below text fields.
      if (canvas.getObjects().some(o => o._isBrandingLogo && o !== img)) {
        const logo = canvas.getObjects().find(o => o._isBrandingLogo);
        const logoIdx = canvas.getObjects().indexOf(logo);
        canvas.moveTo(img, logoIdx + 1);
      } else {
        canvas.sendToBack(img);
      }
      canvas.requestRenderAll();
      resolve(img);
    }, { crossOrigin: 'anonymous' });
  });
}

// Read back a single overlay's current canvas position. If overlayId is
// omitted, reads the first one — preserving old single-overlay callers.
export function readOverlayPosition(canvas, naturalW, naturalH, overlayId) {
  const img = overlayId
    ? canvas.getObjects().find(o => o._isOverlayImage && o._overlayId === overlayId)
    : canvas.getObjects().find(o => o._isOverlayImage);
  if (!img) return null;
  const targetWidth = img.width * (img.scaleX || 1);
  return {
    xPercent: (img.left / naturalW) * 100,
    yPercent: (img.top / naturalH) * 100,
    rotation: img.angle || 0,
    scalePercent: (targetWidth / naturalW) * 100,
  };
}

export function removeOverlayFromCanvas(canvas, overlayId) {
  if (overlayId) {
    const img = canvas.getObjects().find(o => o._isOverlayImage && o._overlayId === overlayId);
    if (img) canvas.remove(img);
  } else {
    const all = canvas.getObjects().filter(o => o._isOverlayImage);
    for (const img of all) canvas.remove(img);
  }
  canvas.requestRenderAll();
}
